import type { SegmentCriteria } from "../../api/types";

/**
 * One labeled criterion group (design.md §5.4: "Detail translates JSONB
 * criteria into labeled groups; raw JSON is never the primary view").
 */
export interface CriteriaGroup {
  readonly label: string;
  readonly value: string;
}

/**
 * Pure function (task 6.5/13.4, "Extract-Before-Mock" pattern — matches
 * `AdminTemplatePreviewPolicy`/`hasRequiredRole` elsewhere in this repo):
 * turns `segment.criterio` JSONB into labeled groups instead of a raw JSON
 * dump (admin-api "Segmentos — lectura", design.md §5.4).
 *
 * Deliberately reads only the two documented `SegmentCriteria` keys
 * (`nivelJerarquico`, `industria`) — task 13.4 REFACTOR: keep this
 * feature-local and never fall back to a generic JSON renderer that would
 * echo back an unrecognized/future key. Tolerates malformed input
 * (`null`/`undefined` criterio, a non-array `nivelJerarquico`, a non-string
 * `industria`, or unrelated extra keys on the object) by simply treating
 * that field as absent rather than throwing (task 13.3 "unknown criteria" /
 * "malformed ... data").
 */
export function describeCriteriaGroups(criterio: SegmentCriteria | null | undefined): CriteriaGroup[] {
  if (!criterio || typeof criterio !== "object") {
    return [];
  }

  const groups: CriteriaGroup[] = [];

  if (Array.isArray(criterio.nivelJerarquico) && criterio.nivelJerarquico.length > 0) {
    groups.push({ label: "Nivel jerárquico", value: criterio.nivelJerarquico.join(", ") });
  }

  if (typeof criterio.industria === "string" && criterio.industria.length > 0) {
    groups.push({ label: "Industria", value: criterio.industria });
  }

  return groups;
}

/** Shared fallback line when a segment has no recognized/active criteria. */
export const NO_CRITERIA_LABEL = "Sin filtros (todos los contactos activos)";

/**
 * Human-readable lines derived from `describeCriteriaGroups` — used by the
 * compact list's criteria summary column and by
 * `features/campaigns/SegmentSelector.tsx`'s recognizable segment metadata
 * (task 9.9 "share... primitives only where semantics match"). Never a raw
 * JSON dump.
 */
export function describeCriteria(criterio: SegmentCriteria): string[] {
  const groups = describeCriteriaGroups(criterio);
  if (groups.length === 0) {
    return [NO_CRITERIA_LABEL];
  }
  return groups.map((group) => `${group.label}: ${group.value}`);
}

/** One-line compact summary for the segments list's "Criterio" column — same source as `describeCriteriaGroups`, never a second criteria model. */
export function summarizeCriteria(criterio: SegmentCriteria | null | undefined): string {
  const groups = describeCriteriaGroups(criterio);
  if (groups.length === 0) {
    return NO_CRITERIA_LABEL;
  }
  return groups.map((group) => `${group.label}: ${group.value}`).join(" · ");
}
